import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./monthlyDistributionChart.module.scss";
import { parseMonths } from "utils/misc";

type MonthlyDistributionProps = {
  data: Array<{ label: string; weight: number }>;
  label: string;
};

const MonthlyDistributionChart: React.FC<MonthlyDistributionProps> = ({
  data,
  label,
}) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const maxWeight = Math.max(...data.map((item) => item.weight));
  const buffer = 0.05; // Fixed buffer of 0.05
  const adjustedMaxWeight = Math.ceil((maxWeight + buffer) * 10) / 10; // Round to next 0.1

  const formattedData = data.map((item) => ({
    month: item.label,
    value: item.weight,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {payload.map((entry: any, index: number) => {
            if (entry.value === undefined) return null;
            return (
              <div key={`value-${index}`}>
                <p
                  style={{
                    color: entry.fill,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                >
                  {parseMonths(entry.payload.month)}
                </p>
                <p
                  style={{
                    color: entry.fill,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                >
                  Distribuzione: {entry.payload.value.toFixed(2)}
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.container}>
      <div style={{}} className={styles.monthlyDistributionTitle}>
        {label}
      </div>
      <ResponsiveContainer width="100%" height="100%" debounce={500}>
        <BarChart
          data={formattedData}
          margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
        >
          <CartesianGrid
            stroke={colorsConfig.red}
            vertical={false}
            horizontal={false}
          />
          <XAxis
            dataKey="month"
            tick={{ fill: colorsConfig.finecoBlack, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: colorsConfig.Gray3 }}
          />
          <YAxis
            domain={[0, adjustedMaxWeight]}
            ticks={Array.from(
              { length: Math.ceil(adjustedMaxWeight / 0.05) + 1 },
              (_, i) => Number((i * 0.05).toFixed(2)),
            )}
            tick={{ fill: colorsConfig.finecoBlack, fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: colorsConfig.Gray3 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey="value"
            fill={colorsConfig.BlueDark}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MonthlyDistributionChart;
